import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {CardContentWithHeader} from 'shells/card/card-content-with-header';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Link} from 'shells/link/link';
import {Body} from 'shells/typography/body';
import {Title} from 'shells/typography/title';

import {CompetitorsList} from './competitors-list';
import {useGetCompetitorIqWebsites, useUpdateCompetitorIqWebsites} from './graphql/hooks';
import {WebsiteField} from './website-field';

import styles from './settings.module.css';

export function Settings() {
    const [website, setWebsite] = React.useState<string>('');
    const [isWebsiteSaving, setIsWebsiteSaving] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null | undefined>(null);

    const {website: savedWebsite, competitors, isLoading} = useGetCompetitorIqWebsites();

    const {updateWebsiteSettings, error} = useUpdateCompetitorIqWebsites();

    React.useEffect(() => {
        if (savedWebsite) {
            setWebsite(savedWebsite);
        }
    }, [savedWebsite]);

    React.useEffect(() => {
        if (error) {
            setErrorMessage(error);
        }
    }, [error]);

    const onSaveWebsite = () => {
        setIsWebsiteSaving(true);
        updateWebsiteSettings({website})
            .then(() => setIsWebsiteSaving(false))
            .catch((e) => {
                setErrorMessage(e);
                setIsWebsiteSaving(false);
            });
    };
    const hasFullCompetitorIq = NutClientConfig.hasFullCompetitorIq;

    return (
        <div>
            <div className='flex justify-sb mb-16 align-center'>
                <div className='flex flex-dir-col flex-grow'>
                    <Title>Settings</Title>
                    <Body>
                        Enter your website and select the competitors you want to compare it against
                    </Body>
                </div>
            </div>
            <div className={styles.container}>
                <CardContentWithHeader
                    title='Website'
                    subtitle='Enter your company’s website to track your online presence and compare
                            it with competitors.'
                    isLoading={isLoading}
                    content={
                        <div className='mb-8'>
                            <WebsiteField
                                website={website}
                                setWebsite={setWebsite}
                                isWebsiteSaving={isWebsiteSaving}
                                errorMessage={errorMessage}
                                setErrorMessage={setErrorMessage}
                                onSaveWebsite={onSaveWebsite}
                                isSaveDisabled={!website || website === savedWebsite}
                            />
                        </div>
                    }
                />
                <CardContentWithHeader
                    title='Competitors'
                    subtitle='Enhance your competitive advantage by adding up to five competitors.
                        Simply enter their websites to gain insights into their web traffic and
                        strategies:'
                    isLoading={isLoading}
                    banner={
                        !hasFullCompetitorIq && (
                            <FlashBanner type='warning' animate={false} hasBorder={true}>
                                <StackLegacy isHorizontal={true} spacing={20} alignCenter={true}>
                                    <Body size='normal' color='blue-dk'>
                                        Adding competitors is only available for Marketing Pro
                                        customers.{' '}
                                        <Link
                                            key='upgrade-plan'
                                            // @ts-expect-error
                                            as={RoutedLink}
                                            variant='primary'
                                            size='normal'
                                            to='/setup/billing/sales'
                                            className='underline'
                                        >
                                            Upgrade your plan
                                        </Link>{' '}
                                        to add and track competitors.
                                    </Body>
                                </StackLegacy>
                            </FlashBanner>
                        )
                    }
                    content={
                        <CompetitorsList
                            competitors={competitors}
                            isInteractive={hasFullCompetitorIq}
                            isDisabled={!hasFullCompetitorIq}
                        />
                    }
                />
            </div>
        </div>
    );
}
